import React, { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import {
    ArrowLeft, MapPin, Bed, Bath, Car, Ruler, Home, Plus, Trash2,
    User, Mail, Phone, Loader2, Check, Building2, ExternalLink,
    ChevronLeft, ChevronRight,
} from "lucide-react";
import toast from "react-hot-toast";
import {
    getSavedCollingsProperty,
    addCollingsPropertyToCRM,
    getCollingsPropertyOwners,
    addCollingsPropertyOwner,
    removeCollingsPropertyOwner,
} from "../../../api/collingsData";

const notFoundImg = "https://atozpropertysolutionsltd.com/property-not-found.svg";

const fmtPrice = (p, display) => {
    if (display) return display;
    if (!p) return "Contact Agent";
    return `$${Number(p).toLocaleString()}`;
};

const emptyOwner = { name: "", email: "", phone: "", mailing_address: "", notes: "" };

const CollingsDataPropertyDetailPage = () => {
    const { id } = useParams();
    const location = useLocation();
    const navigate = useNavigate();

    // Preview mode = browsing an unsaved scraped property (snapshot via router state)
    const isPreview = location.pathname.includes("/preview/");
    const [property, setProperty] = useState(location.state?.property || null);
    const [loading, setLoading] = useState(!isPreview);
    const [saving, setSaving] = useState(false);
    const [activePhoto, setActivePhoto] = useState(0);
    const thumbStripRef = useRef(null);

    const goToPhoto = useCallback((index) => {
        setActivePhoto(index);
        const thumb = thumbStripRef.current?.children?.[index];
        if (thumb) thumb.scrollIntoView({ behavior: "smooth", inline: "center", block: "nearest" });
    }, []);

    const [owners, setOwners] = useState([]);
    const [showOwnerForm, setShowOwnerForm] = useState(false);
    const [ownerForm, setOwnerForm] = useState(emptyOwner);
    const [ownerSaving, setOwnerSaving] = useState(false);

    const savedId = !isPreview ? id : property?.saved_id;

    const loadSaved = useCallback(async () => {
        setLoading(true);
        try {
            const res = await getSavedCollingsProperty(id);
            setProperty(res.data);
            setOwners(res.data.owners || []);
        } catch (err) {
            toast.error("Failed to load property profile");
        } finally {
            setLoading(false);
        }
    }, [id]);

    useEffect(() => {
        if (!isPreview) loadSaved();
    }, [isPreview, loadSaved]);

    const handleAddToCRM = async () => {
        if (!property) return;
        setSaving(true);
        try {
            const res = await addCollingsPropertyToCRM(property);
            const newId = res.data?.property?.id;
            toast.success("Added to CRM");
            navigate(`/collings-data-properties/${newId}`, { replace: true });
        } catch (err) {
            toast.error(err.response?.data?.error || "Failed to add to CRM");
        } finally {
            setSaving(false);
        }
    };

    const handleAddOwner = async (e) => {
        e.preventDefault();
        if (!ownerForm.name.trim()) {
            toast.error("Owner name is required");
            return;
        }
        setOwnerSaving(true);
        try {
            await addCollingsPropertyOwner(savedId, ownerForm);
            toast.success("Owner attached");
            setOwnerForm(emptyOwner);
            setShowOwnerForm(false);
            const res = await getCollingsPropertyOwners(savedId);
            setOwners(res.data || []);
        } catch (err) {
            toast.error(err.response?.data?.error || "Failed to attach owner");
        } finally {
            setOwnerSaving(false);
        }
    };

    const handleRemoveOwner = async (ownerId) => {
        try {
            await removeCollingsPropertyOwner(savedId, ownerId);
            setOwners((prev) => prev.filter((o) => o.id !== ownerId));
            toast.success("Owner detached");
        } catch (err) {
            toast.error("Failed to detach owner");
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-screen">
                <Loader2 className="h-8 w-8 animate-spin text-teal-600" />
            </div>
        );
    }
    if (!property) {
        return (
            <div className="max-w-3xl mx-auto p-8 text-center">
                <p className="text-gray-600">Property not found.</p>
                <button onClick={() => navigate("/collings-data-properties")} className="mt-4 text-teal-600 underline">
                    Back to Property Database
                </button>
            </div>
        );
    }

    const photos = property.photos?.length ? property.photos : (property.images || []).map((i) => i.file);

    return (
        <div className="min-h-screen bg-gray-50">
            <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
                <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4">
                    <ArrowLeft className="h-4 w-4" /> Back
                </button>

                {/* Status banner */}
                {isPreview ? (
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6 flex items-center justify-between flex-wrap gap-3">
                        <p className="text-amber-800 text-sm">
                            This property is <strong>not yet in the CRM</strong>. Add it to create a profile you can attach owners to.
                        </p>
                        <button
                            onClick={handleAddToCRM}
                            disabled={saving}
                            className="px-5 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-60 flex items-center gap-2"
                        >
                            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
                            Add to CRM
                        </button>
                    </div>
                ) : (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-6 flex items-center gap-2">
                        <Check className="h-4 w-4 text-green-600" />
                        <span className="text-green-800 text-sm font-medium">Saved CRM property profile</span>
                    </div>
                )}

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* Left: gallery + details */}
                    <div className="lg:col-span-2 space-y-6">
                        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                            <div className="relative group">
                                <img
                                    src={photos[activePhoto] || notFoundImg}
                                    alt={property.title}
                                    className="w-full h-80 object-cover"
                                    onError={(e) => { e.target.src = notFoundImg; }}
                                />
                                {photos.length > 1 && (
                                    <>
                                        <button
                                            type="button"
                                            onClick={() => goToPhoto((activePhoto - 1 + photos.length) % photos.length)}
                                            aria-label="Previous photo"
                                            className="absolute left-3 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white rounded-full p-2 transition-opacity opacity-0 group-hover:opacity-100 focus:opacity-100"
                                        >
                                            <ChevronLeft className="h-5 w-5" />
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => goToPhoto((activePhoto + 1) % photos.length)}
                                            aria-label="Next photo"
                                            className="absolute right-3 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white rounded-full p-2 transition-opacity opacity-0 group-hover:opacity-100 focus:opacity-100"
                                        >
                                            <ChevronRight className="h-5 w-5" />
                                        </button>
                                        <div className="absolute bottom-3 right-3 bg-black/60 text-white text-xs font-medium px-2 py-1 rounded-full">
                                            {activePhoto + 1} / {photos.length}
                                        </div>
                                    </>
                                )}
                            </div>
                            {photos.length > 1 && (
                                <div ref={thumbStripRef} className="flex gap-2 p-3 overflow-x-auto">
                                    {photos.map((url, i) => (
                                        <img
                                            key={i}
                                            src={url}
                                            alt=""
                                            onClick={() => goToPhoto(i)}
                                            className={`h-16 w-24 object-cover rounded cursor-pointer flex-shrink-0 ${i === activePhoto ? "ring-2 ring-teal-500" : ""}`}
                                            onError={(e) => { e.target.src = notFoundImg; }}
                                        />
                                    ))}
                                </div>
                            )}
                        </div>

                        <div className="bg-white rounded-xl border border-gray-200 p-6">
                            <h1 className="text-2xl font-bold text-gray-900">{fmtPrice(property.price, property.price_display)}</h1>
                            <h2 className="text-lg text-gray-800 mt-1">{property.title}</h2>
                            <p className="text-gray-600 flex items-center gap-1 mt-2">
                                <MapPin className="h-4 w-4" /> {property.address}
                            </p>
                            <div className="flex flex-wrap gap-6 mt-4 text-gray-700">
                                <span className="flex items-center gap-2"><Bed className="h-5 w-5" /> {property.beds || 0} Beds</span>
                                <span className="flex items-center gap-2"><Bath className="h-5 w-5" /> {property.baths || 0} Baths</span>
                                <span className="flex items-center gap-2"><Car className="h-5 w-5" /> {property.parkings || 0} Cars</span>
                                {property.land_size ? <span className="flex items-center gap-2"><Ruler className="h-5 w-5" /> {property.land_size} m²</span> : null}
                                {property.property_type ? <span className="flex items-center gap-2"><Home className="h-5 w-5" /> {property.property_type}</span> : null}
                            </div>
                            {property.description ? (
                                <p className="text-gray-600 mt-4 whitespace-pre-line text-sm leading-relaxed">{property.description}</p>
                            ) : null}
                            {property.listing_url ? (
                                <a href={property.listing_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-teal-600 text-sm mt-4 hover:underline">
                                    View original listing <ExternalLink className="h-3 w-3" />
                                </a>
                            ) : null}
                        </div>
                    </div>

                    {/* Right: meta + owners */}
                    <div className="space-y-6">
                        <div className="bg-white rounded-xl border border-gray-200 p-6">
                            <h3 className="font-semibold text-gray-900 mb-3">Listing Info</h3>
                            <dl className="text-sm space-y-2">
                                {property.agency_name ? <div className="flex justify-between"><dt className="text-gray-500">Source Agency</dt><dd className="text-gray-800 text-right">{property.agency_name}</dd></div> : null}
                                {property.listing_status ? <div className="flex justify-between"><dt className="text-gray-500">Status</dt><dd className="text-gray-800">{property.listing_status}</dd></div> : null}
                                {property.suburb ? <div className="flex justify-between"><dt className="text-gray-500">Suburb</dt><dd className="text-gray-800">{property.suburb} {property.state}</dd></div> : null}
                                {property.postal_code || property.postcode ? <div className="flex justify-between"><dt className="text-gray-500">Postcode</dt><dd className="text-gray-800">{property.postal_code || property.postcode}</dd></div> : null}
                                {property.rental_yield ? <div className="flex justify-between"><dt className="text-gray-500">Est. Yield</dt><dd className="text-gray-800">{property.rental_yield}%</dd></div> : null}
                            </dl>
                        </div>

                        {/* Owners — only for saved profiles */}
                        <div className="bg-white rounded-xl border border-gray-200 p-6">
                            <div className="flex items-center justify-between mb-3">
                                <h3 className="font-semibold text-gray-900 flex items-center gap-2"><Building2 className="h-4 w-4" /> Owners</h3>
                                {!isPreview && (
                                    <button onClick={() => setShowOwnerForm(!showOwnerForm)} className="text-teal-600 text-sm flex items-center gap-1 hover:underline">
                                        <Plus className="h-4 w-4" /> Add
                                    </button>
                                )}
                            </div>

                            {isPreview ? (
                                <p className="text-sm text-gray-500">Add this property to the CRM to attach owner details.</p>
                            ) : (
                                <>
                                    {owners.length === 0 && !showOwnerForm && (
                                        <p className="text-sm text-gray-500">No owners attached yet.</p>
                                    )}
                                    <div className="space-y-3">
                                        {owners.map((o) => (
                                            <div key={o.id} className="border border-gray-100 rounded-lg p-3 bg-gray-50">
                                                <div className="flex items-start justify-between">
                                                    <div>
                                                        <p className="font-medium text-gray-900 flex items-center gap-1"><User className="h-4 w-4" /> {o.name}</p>
                                                        {o.email ? <p className="text-xs text-gray-600 flex items-center gap-1 mt-1"><Mail className="h-3 w-3" /> {o.email}</p> : null}
                                                        {o.phone ? <p className="text-xs text-gray-600 flex items-center gap-1 mt-1"><Phone className="h-3 w-3" /> {o.phone}</p> : null}
                                                    </div>
                                                    <button onClick={() => handleRemoveOwner(o.id)} className="text-gray-400 hover:text-red-500">
                                                        <Trash2 className="h-4 w-4" />
                                                    </button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>

                                    {showOwnerForm && (
                                        <form onSubmit={handleAddOwner} className="mt-4 space-y-2 border-t border-gray-100 pt-4">
                                            <input className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" placeholder="Owner name *" value={ownerForm.name} onChange={(e) => setOwnerForm({ ...ownerForm, name: e.target.value })} />
                                            <input className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" placeholder="Email" value={ownerForm.email} onChange={(e) => setOwnerForm({ ...ownerForm, email: e.target.value })} />
                                            <input className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" placeholder="Phone" value={ownerForm.phone} onChange={(e) => setOwnerForm({ ...ownerForm, phone: e.target.value })} />
                                            <input className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" placeholder="Mailing address" value={ownerForm.mailing_address} onChange={(e) => setOwnerForm({ ...ownerForm, mailing_address: e.target.value })} />
                                            <div className="flex gap-2">
                                                <button type="submit" disabled={ownerSaving} className="flex-1 px-3 py-2 bg-teal-600 text-white rounded-lg text-sm hover:bg-teal-700 disabled:opacity-60 flex items-center justify-center gap-1">
                                                    {ownerSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Save Owner
                                                </button>
                                                <button type="button" onClick={() => { setShowOwnerForm(false); setOwnerForm(emptyOwner); }} className="px-3 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm">
                                                    Cancel
                                                </button>
                                            </div>
                                        </form>
                                    )}
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CollingsDataPropertyDetailPage;
